// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { ReactNode, startTransition, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Menu, X } from 'lucide-react';
import { useSession, hasAnyRole, type SessionState } from '../context/SessionContext';
import { useProductVariant } from '../context/variant';
import { adminNavGroups, dashboardItem, mobilePriorityItems, type NavItem } from '../config/navConfig';
import NavGroup from './NavGroup';
import Logo from './Logo';
import ClubSwitcher from './ClubSwitcher';
import OfflineBanner from './OfflineBanner';
import { useNavExpanded } from '../hooks/useNavExpanded';
import { useAppVersion } from '../hooks/useAppVersion';

type Props = {
  children: ReactNode;
};

/** Filter a single nav item based on variant and roles */
function isItemVisible(item: NavItem, productVariant: string, session: SessionState | null): boolean {
  if (item.variant && item.variant !== productVariant) return false;
  if (!item.requiredRoles || item.requiredRoles.length === 0) return true;
  return hasAnyRole(session, item.requiredRoles);
}

export default function Layout({ children }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { session, clearSession } = useSession();
  const productVariant = useProductVariant();
  const { isExpanded, toggleGroup } = useNavExpanded();
  const appVersion = useAppVersion();

  const handleLogout = () => {
    // react-router 7 applies navigate() inside a React transition. Clearing the session
    // as an urgent update would re-render the still-current route with no session, and the
    // enclosing ProtectedRoute's redirect (to /portal) would supersede this navigation. Put
    // the clear in the same transition so both commit together and /login renders.
    navigate('/login', { replace: true });
    startTransition(() => clearSession());
  };

  const isActive = (path: string) => pathname === path;

  // Filter nav groups: remove invisible items, remove empty groups
  const visibleGroups = useMemo(() => {
    return adminNavGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => isItemVisible(item, productVariant, session)),
      }))
      .filter((group) => group.items.length > 0);
  }, [session, productVariant]);

  // Mobile bottom nav: filtered priority items
  const visibleMobileItems = useMemo(() => {
    return mobilePriorityItems.filter((item) => isItemVisible(item, productVariant, session));
  }, [session, productVariant]);

  const showAlphaBanner = import.meta.env.VITE_ALPHA_BANNER !== 'false';

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900">
      {showAlphaBanner && (
        <div role="status" className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-800">
          <strong>Alpha</strong> — This is an early preview. Features may change or break. Your feedback helps us build something great.
        </div>
      )}
      <div className="flex flex-1">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-slate-200 lg:bg-white">
        <div className="flex h-16 items-center border-b border-slate-200 px-6">
          <div className="min-w-0">
            <Logo variant="full" size="md" />
            {session?.club?.name && (
              <ClubSwitcher currentClub={session.club} className="text-xs text-slate-500 truncate" />
            )}
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" aria-label={t('nav.mainNavigation')}>
          {/* Dashboard (top-level, no group) */}
          <Link
            to={dashboardItem.path}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
              isActive(dashboardItem.path)
                ? 'bg-brand-100 text-brand-800'
                : 'text-slate-700 hover:bg-slate-100'
            }`}
            aria-current={isActive(dashboardItem.path) ? 'page' : undefined}
          >
            <dashboardItem.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            {t(dashboardItem.labelKey)}
          </Link>

          {/* Grouped nav sections */}
          {visibleGroups.map((group) => (
            <NavGroup
              key={group.id}
              label={t(group.labelKey)}
              icon={group.icon}
              items={group.items.map((item) => ({
                path: item.path,
                label: t(item.labelKey),
                icon: item.icon,
              }))}
              isExpanded={isExpanded(group.id, group.defaultOpen)}
              onToggle={() => toggleGroup(group.id)}
            />
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            aria-label={t('common.logOut')}
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
            {t('common.logOut')}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="flex min-w-0 flex-1 flex-col lg:ml-0">
        <header className="sticky top-0 z-10 bg-white shadow-sm lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="min-w-0">
              <Logo variant="full" size="md" />
              {session?.club?.name && (
                <ClubSwitcher currentClub={session.club} className="text-xs text-slate-500 truncate" />
              )}
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              aria-label={t('nav.toggleMenu')}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
          {mobileMenuOpen && (
            <nav className="border-t border-slate-200 bg-white px-4 py-2 max-h-[70vh] overflow-y-auto" aria-label={t('nav.mobileNavigation')}>
              {/* Dashboard */}
              <Link
                to={dashboardItem.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive(dashboardItem.path)
                    ? 'bg-brand-100 text-brand-800'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
                aria-current={isActive(dashboardItem.path) ? 'page' : undefined}
              >
                <dashboardItem.icon className="h-5 w-5" aria-hidden="true" />
                {t(dashboardItem.labelKey)}
              </Link>

              {/* Grouped items in mobile menu */}
              {visibleGroups.map((group) => (
                <NavGroup
                  key={group.id}
                  label={t(group.labelKey)}
                  icon={group.icon}
                  items={group.items.map((item) => ({
                    path: item.path,
                    label: t(item.labelKey),
                    icon: item.icon,
                  }))}
                  isExpanded={isExpanded(group.id, group.defaultOpen)}
                  onToggle={() => toggleGroup(group.id)}
                />
              ))}

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                aria-label={t('common.logOut')}
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                {t('common.logOut')}
              </button>
            </nav>
          )}
        </header>

        {/* Offline indicator */}
        <OfflineBanner />

        {/* Main Content */}
        <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-6 lg:px-6">{children}</main>

        {/* Footer */}
        <footer className="hidden lg:block border-t border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{t('common.copyright', { year: new Date().getFullYear() })}{appVersion && ` · ${appVersion}`}</span>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-brand-600">{t('common.terms')}</Link>
              <Link to="/privacy" className="hover:text-brand-600">{t('common.privacy')}</Link>
              <Link to="/cookies" className="hover:text-brand-600">{t('common.cookies')}</Link>
            </div>
          </div>
        </footer>

        {/* Mobile Bottom Navigation — priority items only */}
        <nav
          className="fixed bottom-0 left-0 right-0 z-10 border-t border-slate-200 bg-white lg:hidden"
          aria-label={t('nav.bottomNavigation')}
        >
          <div className="grid grid-cols-5 gap-0">
            {visibleMobileItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex flex-col items-center justify-center
                  min-h-[56px] px-1 py-2
                  transition-colors
                  ${isActive(item.path)
                    ? 'text-brand-700 bg-brand-50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 active:bg-slate-100'
                  }
                `}
                aria-current={isActive(item.path) ? 'page' : undefined}
                aria-label={t(item.labelKey)}
                title={t(item.labelKey)}
              >
                <item.icon className="h-6 w-6" aria-hidden="true" />
                <span className="mt-1 text-[10px] font-medium leading-none">{t(item.labelKey)}</span>
              </Link>
            ))}
          </div>
        </nav>

        {/* Spacer for bottom nav on mobile */}
        <div className="h-16 lg:hidden" aria-hidden="true" />
      </div>
      </div>
    </div>
  );
}
